<template>
  <div class="d-flex flex-column align-items-center fade-in">
    <content-header title="My Teams" :sub-title="subTitle" class="w-100 mxw-md px-10"/>

    <content-body-card class="w-100 py-20">
      <div v-if="loading" class="text-center py-50">
        <i class="el-icon-loading"></i> Loading teams...
      </div>

      <div v-else-if="members.length === 0" class="text-center py-50">
        <p>No members found.</p>
      </div>

      <div v-else>
        <!-- Selector Row -->
        <div class="selector-row mb-30">
          <div class="selector-card">
            <div class="selector-item">
              <label class="selector-label">
                <i class="el-icon-user"></i> Team Member
              </label>
              <el-select
                v-model="selectedMemberId"
                placeholder="Choose a member..."
                class="selector-dropdown"
                @change="onMemberChange">
                <el-option
                  v-for="member in members"
                  :key="member._id"
                  :label="`${member.profile.firstName} ${member.profile.lastName}`"
                  :value="member._id">
                  <span class="member-name-simple">{{ member.profile.firstName }} {{ member.profile.lastName }}</span>
                </el-option>
              </el-select>
            </div>

            <div v-if="selectedMember && availableSeasons.length > 0" class="selector-item">
              <label class="selector-label">
                <i class="el-icon-trophy"></i> Season
              </label>
              <el-select
                v-model="selectedSeasonName"
                placeholder="Choose a season..."
                class="selector-dropdown"
                clearable
                @change="onSeasonChange">
                <el-option
                  v-for="season in availableSeasons"
                  :key="season.name"
                  :label="season.name"
                  :value="season.name">
                </el-option>
              </el-select>
            </div>

            <div v-if="selectedMember && allCompetitions.length > 0" class="selector-item">
              <label class="selector-label">
                <i class="el-icon-menu"></i> Competition
              </label>
              <el-select
                v-model="selectedCompetition"
                placeholder="All competitions"
                class="selector-dropdown"
                clearable
                @change="onCompetitionChange">
                <el-option
                  v-for="competition in allCompetitions"
                  :key="competition.name"
                  :label="competition.displayName"
                  :value="competition.name"
                  :disabled="competition.filtered">
                </el-option>
              </el-select>
            </div>
          </div>
        </div>

        <!-- Selected Member Data -->
        <div v-if="selectedMember" class="selected-member-section">

          <!-- Performance Data -->
          <div v-if="selectedMember.performance && selectedMember.performance.length > 0">

            <div class="performance-by-season">
              <div v-if="selectedSeason" class="season-section mb-30" :ref="`season-${selectedSeason.name}`" :data-section="`Season ${selectedSeason.name}`">
                <div class="season-header">
                  <div class="season-title-section">
                    <h3 class="season-title"><i class="el-icon-trophy"></i> {{ selectedSeason.name }}</h3>
                    <div class="season-meta">
                      <span class="season-stat">{{ selectedSeason.teams.length }} teams</span>
                      <span class="season-stat">{{ selectedSeason.teams.reduce((sum, team) => sum + team.matches.length, 0) }} matches</span>
                    </div>
                  </div>
                </div>

                <div class="teams-table-container">
                  <el-table
                    :data="selectedSeason.teams"
                    class="teams-table"
                    :expand-row-keys="expandedRows"
                    row-key="teamId"
                    @expand-change="handleExpandChange">

                    <el-table-column type="expand" width="50">
                      <template slot-scope="scope">
                        <div class="match-history-section">
                          <div class="match-history-header">
                            <h6><i class="el-icon-document"></i> Match History</h6>
                            <div class="match-summary">
                              <div class="summary-item wins">{{ scope.row.wins }}W</div>
                              <div class="summary-item draws">{{ scope.row.draws }}D</div>
                              <div class="summary-item losses">{{ scope.row.matches.length - scope.row.wins - scope.row.draws }}L</div>
                            </div>
                          </div>
                          <div v-if="getTeamContact(scope.row.matches, scope.row.apiTeamData)" class="team-contact-expanded">
                            <h6><i class="el-icon-phone-outline"></i> Team Contact</h6>
                            <div class="contact-details">
                              <div v-if="getTeamContact(scope.row.matches, scope.row.apiTeamData).name" class="contact-item">
                                <i class="el-icon-user"></i> {{ getTeamContact(scope.row.matches, scope.row.apiTeamData).name }}
                              </div>
                              <div v-if="getTeamContact(scope.row.matches, scope.row.apiTeamData).email" class="contact-item">
                                <i class="el-icon-message"></i> {{ getTeamContact(scope.row.matches, scope.row.apiTeamData).email }}
                              </div>
                              <div v-if="getTeamContact(scope.row.matches, scope.row.apiTeamData).number" class="contact-item">
                                <i class="el-icon-phone"></i> {{ getTeamContact(scope.row.matches, scope.row.apiTeamData).number }}
                              </div>
                            </div>
                          </div>
                          <div class="matches-grid">
                            <div v-for="match in getFilteredMatches(scope.row.matches, scope.row.teamId).slice(0, 10)" :key="match._id" class="match-card">
                              <div class="match-card-header">
                                <div class="match-info">
                                  <div class="match-date">{{ formatMatchDate(match.dateTime) }}</div>
                                  <div class="match-competition">
                                    {{ match.competition?.name || 'Competition' }}
                                    <el-tooltip
                                      v-if="match.competition?.selectedEvent || match.competition?.selectedSeason"
                                      :content="getSquadTooltip(match.competition)"
                                      placement="top">
                                      <span class="squad-indicator">
                                        <i class="el-icon-user-solid" :class="{ 'squad-eligible': match.competition?.memberWasInSquad !== false }"></i>
                                      </span>
                                    </el-tooltip>
                                  </div>
                                </div>
                                <div class="match-result-badge" :class="getMatchResultClass(match, scope.row.teamId)">
                                  {{ getMatchResult(match, scope.row.teamId) }}
                                </div>
                              </div>
                              <div class="match-card-body">
                                <div class="match-teams-stacked">
                                  <div class="team-score-row">
                                    <span class="team-name">{{ getMatchTeamsDisplay(match).home }}</span>
                                    <span class="team-score">{{ getMatchTeamsDisplay(match).homeScore }}</span>
                                  </div>
                                  <div class="team-score-row">
                                    <span class="team-name">{{ getMatchTeamsDisplay(match).away }}</span>
                                    <span class="team-score">{{ getMatchTeamsDisplay(match).awayScore }}</span>
                                  </div>
                                </div>
                              </div>

                              <!-- Player Performance Section -->
                              <div v-if="getPlayerPerformance(match, selectedMemberId).length > 0 || getPlayerOfTheMatchPoints(match, selectedMemberId) > 0" class="player-performance">
                                <div class="performance-header">
                                  <i class="el-icon-star-on"></i>
                                  <span>Player Performance</span>
                                </div>
                                <!-- Match Events (Goals, Tries, etc.) -->
                                <div v-if="getAggregatedPlayerEvents(match, selectedMemberId).length > 0" class="performance-events">
                                  <div v-for="event in getAggregatedPlayerEvents(match, selectedMemberId)" :key="event.type + '-' + event.name" class="performance-event">
                                    <div class="event-badge" :class="getEventTypeClass(event.type, event.name)">
                                      <span class="event-name">{{ event.name }}</span>
                                      <span class="event-count" v-if="event.count > 1">{{ event.count }}</span>
                                    </div>
                                    <!-- Event points earned (e.g., +5 for a try, +2 for conversion) -->
                                    <div class="event-value" v-if="event.totalValue > 0">+{{ event.totalValue }}</div>
                                  </div>
                                </div>
                                <!-- Man of the Match Awards -->
                                <div v-if="getPlayerOfTheMatchPoints(match, selectedMemberId) > 0" class="motm-section">
                                  <div class="motm-badge">
                                    <i class="el-icon-trophy"></i>
                                    <span>Man of the Match</span>
                                  </div>
                                  <div class="motm-points">{{ getPlayerOfTheMatchPoints(match, selectedMemberId) }} pts</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="getFilteredMatches(scope.row.matches, scope.row.teamId).length > 10" class="show-more">
                            <span>Showing 10 of {{ getFilteredMatches(scope.row.matches, scope.row.teamId).length }} matches</span>
                          </div>
                        </div>
                      </template>
                    </el-table-column>

                    <el-table-column label="Team" min-width="250">
                      <template slot-scope="scope">
                        <div class="team-cell">
                          <div class="team-logo">
                            <img v-if="scope.row.logo" :src="scope.row.logo" :alt="scope.row.name" class="logo-img">
                            <div v-else class="logo-placeholder">{{ scope.row.name.charAt(0) }}</div>
                          </div>
                          <div class="team-info">
                            <div class="team-name">{{ scope.row.name }}</div>
                            <div class="team-meta">
                              <span class="team-joined"><i class="el-icon-calendar"></i> Joined {{ formatDate(scope.row.addedAt) }}</span>
                            </div>
                          </div>
                        </div>
                      </template>
                    </el-table-column>

                    <el-table-column label="Matches" width="100" align="center">
                      <template slot-scope="scope">
                        <div class="stat-display">
                          <div class="stat-number">{{ scope.row.matches.length }}</div>
                        </div>
                      </template>
                    </el-table-column>

                  </el-table>
                </div>

                <!-- Mobile-friendly card layout -->
                <div class="mobile-teams-list">
                  <div v-for="team in selectedSeason.teams" :key="team.teamId" class="mobile-team-card">
                    <div class="mobile-team-header" @click="toggleTeamExpansion(team.teamId)">
                      <div class="mobile-team-info">
                        <div class="mobile-team-logo">
                          <img v-if="team.logo" :src="team.logo" :alt="team.name" class="mobile-logo-img">
                          <div v-else class="mobile-logo-placeholder">{{ team.name.charAt(0) }}</div>
                        </div>
                        <div class="mobile-team-details">
                          <div class="mobile-team-name">{{ team.name }}</div>
                          <div class="mobile-team-meta">Joined {{ formatDate(team.addedAt) }}</div>
                        </div>
                      </div>
                      <div class="mobile-team-stats">
                        <div class="mobile-stat">
                          <span class="mobile-stat-number">{{ team.matches.length }}</span>
                          <span class="mobile-stat-label">Matches</span>
                        </div>
                      </div>
                      <i class="el-icon-arrow-down mobile-expand-icon" :class="{ 'expanded': isTeamExpanded(team.teamId) }"></i>
                    </div>

                    <div v-if="isTeamExpanded(team.teamId)" class="mobile-match-history">
                      <div class="mobile-match-header">
                        <h6><i class="el-icon-document"></i> Match History</h6>
                        <div class="mobile-match-summary">
                          <div class="summary-item wins">{{ team.wins }}W</div>
                          <div class="summary-item draws">{{ team.draws }}D</div>
                          <div class="summary-item losses">{{ team.matches.length - team.wins - team.draws }}L</div>
                        </div>
                      </div>
                      <div v-if="getTeamContact(team.matches, team.apiTeamData)" class="mobile-team-contact-expanded">
                        <h6><i class="el-icon-phone-outline"></i> Team Contact</h6>
                        <div class="mobile-contact-details">
                          <div v-if="getTeamContact(team.matches, team.apiTeamData).name" class="mobile-contact-item">
                            <i class="el-icon-user"></i> {{ getTeamContact(team.matches, team.apiTeamData).name }}
                          </div>
                          <div v-if="getTeamContact(team.matches, team.apiTeamData).email" class="mobile-contact-item">
                            <i class="el-icon-message"></i> {{ getTeamContact(team.matches, team.apiTeamData).email }}
                          </div>
                          <div v-if="getTeamContact(team.matches, team.apiTeamData).number" class="mobile-contact-item">
                            <i class="el-icon-phone"></i> {{ getTeamContact(team.matches, team.apiTeamData).number }}
                          </div>
                        </div>
                      </div>
                      <div class="mobile-matches-grid">
                        <div v-for="match in getFilteredMatches(team.matches, team.teamId).slice(0, 10)" :key="match._id" class="mobile-match-card">
                          <div class="mobile-match-header-info">
                            <div class="mobile-match-date">{{ formatMatchDate(match.dateTime) }}</div>
                            <div class="mobile-match-result" :class="getMatchResultClass(match, team.teamId)">
                              {{ getMatchResult(match, team.teamId) }}
                            </div>
                          </div>
                          <div class="mobile-match-details">
                            <div class="mobile-match-teams-stacked">
                              <div class="mobile-team-score-row">
                                <span class="mobile-team-name">{{ getMatchTeamsDisplay(match).home }}</span>
                                <span class="mobile-team-score">{{ getMatchTeamsDisplay(match).homeScore }}</span>
                              </div>
                              <div class="mobile-team-score-row">
                                <span class="mobile-team-name">{{ getMatchTeamsDisplay(match).away }}</span>
                                <span class="mobile-team-score">{{ getMatchTeamsDisplay(match).awayScore }}</span>
                              </div>
                            </div>
                          </div>
                          <div v-if="getPlayerPerformance(match, selectedMemberId).length > 0 || getPlayerOfTheMatchPoints(match, selectedMemberId) > 0" class="mobile-player-performance">
                            <div class="mobile-performance-header">
                              <i class="el-icon-star-on"></i>
                              <span>Performance</span>
                            </div>
                            <!-- Match Events (Goals, Tries, etc.) -->
                            <div v-if="getAggregatedPlayerEvents(match, selectedMemberId).length > 0" class="mobile-performance-events">
                              <div v-for="event in getAggregatedPlayerEvents(match, selectedMemberId)" :key="event.type + '-' + event.name" class="mobile-performance-event">
                                <div class="mobile-event-badge" :class="getEventTypeClass(event.type, event.name)">
                                  <span class="mobile-event-name">{{ event.name }}</span>
                                  <span class="mobile-event-count" v-if="event.count > 1">{{ event.count }}</span>
                                </div>
                                <!-- Event points earned (e.g., +5 for a try, +2 for conversion) -->
                                <div class="mobile-event-value" v-if="event.totalValue > 0">+{{ event.totalValue }}</div>
                              </div>
                            </div>
                            <!-- Man of the Match Awards -->
                            <div v-if="getPlayerOfTheMatchPoints(match, selectedMemberId) > 0" class="mobile-motm-section">
                              <div class="mobile-motm-badge">
                                <i class="el-icon-trophy"></i>
                                <span>MotM</span>
                              </div>
                              <div class="mobile-motm-points">{{ getPlayerOfTheMatchPoints(match, selectedMemberId) }} pts</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="getFilteredMatches(team.matches, team.teamId).length > 10" class="mobile-show-more">
                        Showing 10 of {{ getFilteredMatches(team.matches, team.teamId).length }} matches
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="no-data-section">
            <div class="no-data-card">
              <div class="no-data-content">
                <i class="el-icon-basketball"></i>
                <h4>No Team Performance Data</h4>
                <p>This member hasn't been assigned to any teams yet.</p>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state-section">
          <div class="empty-state-card">
            <div class="empty-content">
              <i class="el-icon-user-solid"></i>
              <h4>Select a Member</h4>
              <p>Choose a member from the dropdown above to view their team performance and match history.</p>
            </div>
          </div>
        </div>
      </div>
    </content-body-card>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'MyTeams',
  data() {
    return {
      loading: true,
      members: [],
      selectedMemberId: null,
      selectedSeasonName: null,
      selectedCompetition: null,
      expandedRows: [],
      expandedMobileTeams: [],
      teamCompetitionFilters: {},
      subTitle: 'View your team memberships, performance statistics, and match history organized by season.',
      currentSection: null,
      scrollProgress: 0,
      intersectionObserver: null,
    };
  },
  async mounted() {
    await this.fetchMemberTeams();
    this.setupIntersectionObserver();
    this.setupScrollProgress();
  },

  beforeDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    window.removeEventListener('scroll', this.handleScrollProgress);
  },
  computed: {
    selectedMember() {
      return this.members.find((member) => member._id === this.selectedMemberId);
    },
    availableSeasons() {
      if (!this.selectedMember || !this.selectedMember.performance) return [];
      return this.getSeasonData(this.selectedMember.performance);
    },
    selectedSeason() {
      if (!this.availableSeasons.length) return null;

      // If no season is selected, return all seasons combined
      if (!this.selectedSeasonName) {
        const allTeams = new Map();

        this.availableSeasons.forEach((season) => {
          season.teams.forEach((team) => {
            if (allTeams.has(team.teamId)) {
              // Merge matches from multiple seasons
              const existingTeam = allTeams.get(team.teamId);
              existingTeam.matches = [...existingTeam.matches, ...team.matches];
              existingTeam.wins += team.wins;
              existingTeam.draws += team.draws;

              // Use earliest addedAt date
              if (!existingTeam.addedAt || (team.addedAt && new Date(team.addedAt) < new Date(existingTeam.addedAt))) {
                existingTeam.addedAt = team.addedAt;
              }
            } else {
              allTeams.set(team.teamId, { ...team });
            }
          });
        });

        return {
          name: 'All Seasons',
          teams: Array.from(allTeams.values())
        };
      }

      const season = this.availableSeasons.find((s) => s.name === this.selectedSeasonName);
      if (!season) return null;

      // Apply competition filter if selected
      if (this.selectedCompetition) {
        const foundComp = this.allCompetitions.find((comp) => comp.name === this.selectedCompetition);

        if (!foundComp) return season;

        // Create a map of existing teams for quick lookup
        const existingTeams = new Map();
        season.teams.forEach((team) => existingTeams.set(team.teamId, team));

        // Add missing teams from the selected competition
        if (foundComp.teams) {
          foundComp.teams.forEach((compTeam) => {
            if (!existingTeams.has(compTeam._id)) {
              existingTeams.set(compTeam._id, {
                teamId: compTeam._id,
                name: compTeam.name,
                logo: compTeam.meta?.imgUrl || null,
                matches: [],
                wins: 0,
                draws: 0,
                addedAt: null,
                firstMatch: null,
                apiTeamData: null,
                winRate: 0
              });
            }
          });
        }

        // Filter teams to only show those in the selected competition
        const filteredTeams = Array.from(existingTeams.values())
          .map((team) => ({
            ...team,
            matches: team.matches.filter((match) => {
              const competitionName = match.competition?.name || 'Unknown Competition';
              return competitionName === this.selectedCompetition;
            })
          }))
          .filter((team) => {
            // Show teams that either have matches OR exist in the selected competition
            if (team.matches.length > 0) return true;

            return foundComp.teams ? foundComp.teams.some((compTeam) => compTeam._id === team.teamId) : false;
          });

        return {
          ...season,
          teams: filteredTeams
        };
      }

      return season;
    },
    allCompetitions() {
      if (!this.selectedMember || !this.selectedMember.performance || !this.selectedSeasonName) return [];

      const competitions = new Map();
      this.selectedMember.performance.forEach((item) => {
        // Show competitions from the selected season only
        const itemSeason = String(item.season) || 'Unknown Season';
        const isInSelectedSeason = itemSeason === this.selectedSeasonName;

        // Only show competitions where member was in squad and from selected season
        const wasInSquad = item.competition?.memberWasInSquad !== false;

        if (item.competition?.name && isInSelectedSeason && wasInSquad) {
          // Only include competitions that match the selected season and where member was in squad
          if (!competitions.has(item.competition.name)) {
            competitions.set(item.competition.name, {
              name: item.competition.name,
              displayName: item.competition.name,
              teams: item.competition.teams || [],
              memberWasInSquad: item.competition.memberWasInSquad,
            });
          }
        }
      });

      return Array.from(competitions.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
  },
  watch: {
    selectedMemberId() {
      this.setDefaultSeason();
    }
  },
  methods: {
    async fetchMemberTeams() {
      try {
        this.loading = true;
        const response = await this.$http.get('/assemble/api/v1/account/portal/my-teams');

        if (response.data.status === 'success') {
          this.members = response.data.data;
          if (this.members.length > 0) {
            this.selectedMemberId = this.members[0]._id;
            this.setDefaultSeason();
          }
        }
      } catch (error) {
        console.error('Error fetching member teams:', error);
        this.$message.error('Failed to load team data');
      } finally {
        this.loading = false;
      }
    },

    setDefaultSeason() {
      if (this.availableSeasons.length > 0) {
        // Sort seasons by name/year and select the latest one
        const sortedSeasons = [...this.availableSeasons].sort((a, b) => b.name.localeCompare(a.name));
        this.selectedSeasonName = sortedSeasons[0].name;
      }
    },

    onSeasonChange() {
      // Reset expanded rows when season changes
      this.expandedRows = [];
      this.expandedMobileTeams = [];
      // Reset competition filter when season changes
      this.selectedCompetition = null;
    },

    onCompetitionChange() {
      // Reset expanded rows when competition filter changes
      this.expandedRows = [];
      this.expandedMobileTeams = [];
    },

    getSeasonData(performance) {
      const seasonMap = new Map();

      performance.forEach((item) => {
        const seasonName = String(item.season) || 'Unknown Season';

        if (!seasonMap.has(seasonName)) {
          seasonMap.set(seasonName, {
            name: seasonName,
            teams: new Map()
          });
        }

        const season = seasonMap.get(seasonName);
        const teamId = item.teamPlayed;

        // Get team name from API data (either from team object or from match data)
        const teamName = item.team?.name || this.getTeamName(item, teamId);

        if (!season.teams.has(teamId)) {
          season.teams.set(teamId, {
            teamId,
            name: teamName,
            logo: this.getTeamLogo(item),
            matches: [],
            wins: 0,
            draws: 0,
            addedAt: item.addedAt, // Use addedAt from API
            firstMatch: item.dateTime,
            apiTeamData: item.team // Store original team data from API
          });
        }

        const team = season.teams.get(teamId);

        // Only process as match if it has match data and is final
        if (item._hasMatches !== false && item.status === 'final') {
          team.matches.push(item);

          const result = this.getMatchResult(item, teamId);
          if (result === 'W') {
            team.wins++;
          } else if (result === 'D') {
            team.draws++;
          }

          if (item.dateTime && (!team.firstMatch || moment(item.dateTime).isBefore(moment(team.firstMatch)))) {
            team.firstMatch = item.dateTime;
          }
        }

        // Update addedAt if this item has an earlier date
        if (item.addedAt && (!team.addedAt || moment(item.addedAt).isBefore(moment(team.addedAt)))) {
          team.addedAt = item.addedAt;
        }

        // Update team API data if it's not set or if this item has team data
        if (!team.apiTeamData && item.team) {
          team.apiTeamData = item.team;
        }
      });

      const seasons = Array.from(seasonMap.values()).map((season) => ({
        ...season,
        teams: Array.from(season.teams.values()).map((team) => ({
          ...team,
          winRate: team.matches.length > 0 ? Math.round((team.wins / team.matches.length) * 100) : 0
        }))
      }));

      return seasons.sort((a, b) => b.name.localeCompare(a.name));
    },

    getTeamName(match, teamId) {
      if (match.homeTeam?._id === teamId) {
        return match.homeTeam.name || 'Home Team';
      } if (match.awayTeam?._id === teamId) {
        return match.awayTeam.name || 'Away Team';
      }
      return 'Unknown Team';
    },

    getTeamLogo(item) {
      // Check if it's from API data first (teams without matches)
      return item.team?.meta?.imgUrl || _.get(item, 'team.meta.imgUrl');
    },

    isWin(match, teamId) {
      if (!match.scores || match.status !== 'final') {
        return false;
      }

      const homeScore = match.scores.homeTeam || 0;
      const awayScore = match.scores.awayTeam || 0;

      if (match.homeTeam?._id === teamId) {
        return homeScore > awayScore;
      } if (match.awayTeam?._id === teamId) {
        return awayScore > homeScore;
      }

      return false;
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY');
    },

    // eslint-disable-next-line no-unused-vars
    handleExpandChange(row, expandedRows) {
      this.expandedRows = expandedRows.map((r) => r.teamId);
    },

    getMatchResult(match, teamId) {
      if (!match.scores || match.status !== 'final') {
        return 'N/A';
      }

      const homeScore = match.scores.homeTeam || 0;
      const awayScore = match.scores.awayTeam || 0;

      if (match.homeTeam?._id === teamId) {
        if (homeScore > awayScore) return 'W';
        if (homeScore < awayScore) return 'L';
        return 'D';
      }
      if (match.awayTeam?._id === teamId) {
        if (awayScore > homeScore) return 'W';
        if (awayScore < homeScore) return 'L';
        return 'D';
      }
      return '';
    },

    getMatchResultClass(match, teamId) {
      const result = this.getMatchResult(match, teamId);
      return {
        'match-win': result === 'W',
        'match-loss': result === 'L',
        'match-draw': result === 'D'
      };
    },

    getMatchTeamsDisplay(match) {
      const home = match.homeTeam?.name || 'Home Team';
      const away = match.awayTeam?.name || 'Away Team';
      return {
        home,
        away,
        homeScore: match.scores?.homeTeam || 0,
        awayScore: match.scores?.awayTeam || 0
      };
    },

    onMemberChange() {
      this.expandedRows = [];
      this.currentSection = null;
      this.scrollProgress = 0;
      // Re-setup intersection observer for new member's sections
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },

    formatMatchDate(dateString) {
      return moment(dateString).format('MMM DD');
    },

    getTotalTeams(performance) {
      if (!performance || performance.length === 0) return 0;
      const uniqueTeams = new Set(performance.map((match) => match.teamPlayed));
      return uniqueTeams.size;
    },

    getTotalMatches(performance) {
      return performance ? performance.length : 0;
    },

    getOverallWinRate(performance) {
      if (!performance || performance.length === 0) return 0;
      const wins = performance.filter((match) => this.isWin(match, match.teamPlayed)).length;
      return Math.round((wins / performance.length) * 100);
    },

    getWinRateColor(winRate) {
      if (winRate >= 70) return '#67C23A';
      if (winRate >= 50) return '#E6A23C';
      return '#F56C6C';
    },

    getPlayerPerformance(match, memberId) {
      if (!match.scoringEvents || !memberId) return [];

      return match.scoringEvents.filter((event) => event.meta?.player?._id === memberId);
    },

    getTotalPlayerPoints(match, memberId) {
      const events = this.getPlayerPerformance(match, memberId);
      return events.reduce((total, event) => total + (event.value || 0), 0);
    },

    getEventTypeClass(type, eventName = null) {
      // Default type-based classes for standard events
      const typeClasses = {
        1: 'event-try',
        2: 'event-conversion',
        3: 'event-penalty',
        4: 'event-drop-goal',
        5: 'event-field-goal'
      };

      // For custom events, use the event name to determine consistent colors
      if (eventName && !typeClasses[type]) {
        // Hash the event name to get consistent color assignment
        let hash = 0;
        for (let i = 0; i < eventName.length; i++) {
          hash = hash * 31 + eventName.charCodeAt(i);
          hash %= 1000000;
        }

        const colorClasses = ['event-goal', 'event-assist', 'event-card', 'event-substitution'];
        return colorClasses[Math.abs(hash) % colorClasses.length];
      }

      return typeClasses[type] || 'event-other';
    },

    getAggregatedPlayerEvents(match, memberId) {
      if (!match.scoringEvents || !memberId) return [];

      const events = match.scoringEvents.filter((event) => event.meta?.player?._id === memberId);
      const aggregatedMap = new Map();

      events.forEach((event) => {
        const key = `${event.type}-${event.name || 'Unknown'}`;

        if (aggregatedMap.has(key)) {
          const existing = aggregatedMap.get(key);
          existing.count += 1;
          existing.totalValue += (event.value || 0);
        } else {
          aggregatedMap.set(key, {
            type: event.type,
            name: event.name || 'Unknown Event',
            count: 1,
            totalValue: event.value || 0
          });
        }
      });

      return Array.from(aggregatedMap.values());
    },

    getPlayerOfTheMatchPoints(match, memberId) {
      if (!match.playerOfTheMatch || !memberId) return 0;

      const motmAward = match.playerOfTheMatch.find((award) => award.player?._id === memberId);

      return motmAward ? (motmAward.value || 0) : 0;
    },

    formatEventTime(event) {
      if (event.min === undefined) return '';
      const minutes = event.min;
      const seconds = event.sec || 0;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },

    toggleTeamExpansion(teamId) {
      const index = this.expandedMobileTeams.indexOf(teamId);
      if (index > -1) {
        this.expandedMobileTeams.splice(index, 1);
      } else {
        this.expandedMobileTeams.push(teamId);
      }
    },

    isTeamExpanded(teamId) {
      return this.expandedMobileTeams.includes(teamId);
    },

    getUniqueCompetitions(matches) {
      const competitions = matches
        .map((match) => match.competition?.name || 'Unknown Competition')
        .filter((comp, index, arr) => arr.indexOf(comp) === index)
        .sort();
      return competitions;
    },

    getFilteredMatches(matches, teamId) {
      const selectedCompetition = this.teamCompetitionFilters[teamId];
      if (!selectedCompetition) {
        // No competition filter, but still filter out matches where member wasn't in squad
        return matches.filter((match) => {
          const wasInSquad = match.competition?.memberWasInSquad !== false;
          return wasInSquad;
        });
      }
      return matches.filter((match) => {
        const competitionName = match.competition?.name || 'Unknown Competition';
        const competitionMatches = competitionName === selectedCompetition;

        // Only include matches where member was in the squad for this competition
        const wasInSquad = match.competition?.memberWasInSquad !== false;

        return competitionMatches && wasInSquad;
      });
    },

    getTeamContact(matches, teamData = null) {
      // If we have team data from API (for teams without matches), use that first
      if (teamData?.contact) {
        return {
          name: teamData.contact.name || null,
          email: teamData.contact.email || null,
          number: teamData.contact.number || null
        };
      }

      // Fallback to getting contact from match data
      if (!matches || matches.length === 0) return null;

      const firstMatch = matches[0];
      const contact = firstMatch.team?.contact;

      if (!contact) return null;

      return {
        name: contact.name || null,
        email: contact.email || null,
        number: contact.number || null
      };
    },

    setupIntersectionObserver() {
      // Disconnect existing observer
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      this.$nextTick(() => {
        const sections = this.$el.querySelectorAll('[data-section]');
        if (sections.length === 0) return;

        // Create intersection observer to track which section is in view
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
                this.currentSection = entry.target.getAttribute('data-section');
              }
            });
          },
          {
            threshold: [0.1, 0.3, 0.5, 0.7],
            rootMargin: '-10% 0px -70% 0px' // Focus on upper part of viewport
          }
        );

        // Observe all sections
        sections.forEach((section) => {
          this.intersectionObserver.observe(section);
        });
      });
    },

    setupScrollProgress() {
      window.addEventListener('scroll', this.handleScrollProgress);
    },

    handleScrollProgress() {
      if (!this.selectedMember) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const memberProfileCard = this.$el.querySelector('.member-profile-card');

      if (memberProfileCard) {
        const profileBottom = memberProfileCard.offsetTop + memberProfileCard.offsetHeight;
        const adjustedScrollTop = Math.max(0, scrollTop - profileBottom);
        const adjustedDocHeight = Math.max(1, docHeight - profileBottom);

        this.scrollProgress = Math.min(100, Math.max(0, (adjustedScrollTop / adjustedDocHeight) * 100));
      }
    },

    getSquadTooltip(competition) {
      if (!competition) return '';

      const { selectedEvent, selectedSeason, memberWasInSquad } = competition;
      const eligibilityText = memberWasInSquad !== false ? 'Squad Member' : 'Not in Squad';

      if (selectedEvent) {
        return `${eligibilityText} - Competition uses Event-based squad selection (Event ID: ${selectedEvent})`;
      }
      if (selectedSeason) {
        return `${eligibilityText} - Competition uses Season-based squad selection (Season: ${selectedSeason})`;
      }

      return eligibilityText;
    }
  }
};
</script>

<style scoped lang="scss">
.squad-indicator {
  margin-left: 8px;
  display: inline-block;
}

.squad-indicator .el-icon-user-solid {
  font-size: 12px;
  padding: 2px;
  border-radius: 50%;
  color: #909399;
  background-color: #f4f4f5;
}

.squad-indicator .el-icon-user-solid.squad-eligible {
  color: #67c23a;
  background-color: #f0f9ff;
}
// Variables
$primary: #da3351;
$success: #10b981;
$danger: #ef4444;
$warning: #f59e0b;
$info: #3b82f6;
$dark: #1f2937;
$gray: #6b7280;
$light-gray: #f9fafb;
$border: #e5e7eb;
$shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
$radius: 12px;
$radius-sm: 8px;

// Mixins
@mixin card {
  background: white;
  border-radius: $radius;
  box-shadow: $shadow;
}

@mixin badge($bg, $color) {
  background: $bg;
  color: $color;
  padding: 0.25rem 0.75rem;
  border-radius: $radius-sm;
  font-weight: 600;
  font-size: 0.875rem;
}

// Selector Section
.selector-row {
  margin-bottom: 2rem;
}

.selector-card {
  @include card;
  padding: 1.5rem;
  display: flex;
  gap: 2rem;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
}

.selector-item {
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: auto;
  }
}

.selector-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: $dark;
  margin-bottom: 0.5rem;

  i {
    margin-right: 0.25rem;
    color: $gray;
  }
}

.selector-dropdown {
  width: 100%;
}

// Season Section
.season-section {
  @include card;
  overflow: hidden;
}

.season-header {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  padding: 1.5rem;
}

.season-title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.season-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: $dark;

  i {
    color: $warning;
  }
}

.season-meta {
  display: flex;
  gap: 1rem;
}

.season-stat {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  color: $gray;
}

// Teams Table
.teams-table-container {
  padding: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
}

.team-cell {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.team-logo {
  flex-shrink: 0;
}

.logo-img {
  width: 40px;
  height: 40px;
  border-radius: $radius-sm;
  object-fit: cover;
}

.logo-placeholder {
  width: 40px;
  height: 40px;
  border-radius: $radius-sm;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.team-info {
  flex: 1;
}

.team-name {
  font-weight: 600;
  color: $dark;
  margin-bottom: 0.25rem;
}

.team-meta {
  font-size: 0.875rem;
  color: $gray;

  i {
    margin-right: 0.25rem;
  }
}

.stat-display {
  text-align: center;
}

.stat-number {
  font-weight: 700;
  font-size: 1.25rem;
  color: $dark;
}

// Match History
.match-history-section {
  padding: 1.5rem;
  background: $light-gray;
}

.match-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h6 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: $dark;

    i {
      margin-right: 0.5rem;
      color: $gray;
    }
  }
}

.match-summary {
  display: flex;
  gap: 0.5rem;
}

.summary-item {
  @include badge(#e5e7eb, $gray);

  &.wins {
    @include badge(#d1fae5, #065f46);
  }

  &.draws {
    @include badge(#fef3c7, #92400e);
  }

  &.losses {
    @include badge(#fee2e2, #991b1b);
  }
}

// Team Contact
.team-contact-expanded {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #fff;
  border-radius: $radius-sm;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  h6 {
    margin: 0 0 0.875rem 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: $dark;
    display: flex;
    align-items: center;

    i {
      margin-right: 0.5rem;
      color: $info;
      padding: 0.25rem;
      background: rgba(59, 130, 246, 0.1);
      border-radius: 4px;
      font-size: 0.75rem;
    }
  }
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: $dark;
  padding: 0.5rem 0.75rem;
  background: #f9f9f9;
  border-radius: 6px;
  // border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.2s ease;

  &:hover {
    // background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  i {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $info;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 4px;
    font-size: 0.75rem;
    flex-shrink: 0;
  }
}

// Competition Filter
.competition-filter-section {
  margin-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
}

.competition-filter {
  width: 250px;
}

// Matches Grid
.matches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.match-card {
  background: white;
  border-radius: $radius-sm;
  padding: 1rem;
  border: 1px solid $border;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.match-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.match-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.match-date {
  font-weight: 600;
  color: $dark;
  font-size: 0.875rem;
}

.match-competition {
  color: $gray;
  font-size: 0.75rem;
}

.match-result-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.75rem;
  min-width: 24px;
  text-align: center;

  &.match-win {
    @include badge(#d1fae5, #065f46);
  }

  &.match-loss {
    @include badge(#fee2e2, #991b1b);
  }

  &.match-draw {
    @include badge(#fef3c7, #92400e);
  }
}

.match-teams-stacked {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.team-score-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: $light-gray;
  border-radius: 4px;

  .team-name {
    color: $dark;
    font-size: 0.875rem;
  }

  .team-score {
    font-weight: 700;
    color: $dark;
  }
}

.show-more {
  text-align: center;
  margin-top: 1rem;
  color: $gray;
  font-size: 0.875rem;
}

// Player Performance
.player-performance {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid $border;
}

.performance-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: $dark;
  font-size: 0.875rem;

  i {
    color: $warning;
  }
}

.performance-events {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.performance-event {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.event-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;

  .event-name {
    line-height: 1;
  }

  .event-count {
    font-size: 0.625rem;
    opacity: 0.8;
    margin-left: 0.25rem;
  }
}

.event-value {
  background: $success;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.75rem;
}

// Event Type Colors
.event-try { @include badge(#dcfce7, #166534); }
.event-conversion { @include badge(#dbeafe, #1e40af); }
.event-penalty { @include badge(#fef3c7, #92400e); }
.event-drop-goal { @include badge(#e0e7ff, #4338ca); }
.event-field-goal { @include badge(#fce7f3, #be185d); }
.event-goal { @include badge(#dcfce7, #166534); }
.event-assist { @include badge(#dbeafe, #1e40af); }
.event-card { @include badge(#fef3c7, #92400e); }
.event-substitution { @include badge(#e0e7ff, #4338ca); }
.event-other { @include badge(#f1f5f9, #475569); }

// Man of the Match
.motm-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, $warning 0%, darken($warning, 10%) 100%);
  border-radius: 6px;
}

.motm-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;

  i {
    color: white;
  }
}

.motm-points {
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
}

// Empty States
.empty-state-section,
.no-data-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.empty-state-card,
.no-data-card {
  @include card;
  padding: 3rem;
  text-align: center;
  max-width: 400px;
}

.empty-content,
.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  i {
    font-size: 3rem;
    color: lighten($gray, 20%);
  }

  h4 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: $dark;
  }

  p {
    margin: 0;
    color: $gray;
  }
}

// Mobile Styles
.mobile-teams-list {
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 1rem;
  }
}

.mobile-team-card {
  @include card;
  margin-bottom: 1rem;
  overflow: hidden;

  @media (max-width: 768px) {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
}

.mobile-team-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:active {
    background: $light-gray;
  }
}

.mobile-team-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.mobile-logo-img,
.mobile-logo-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.mobile-logo-placeholder {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.mobile-team-details {
  flex: 1;
}

.mobile-team-name {
  font-weight: 600;
  color: $dark;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.mobile-team-meta {
  color: $gray;
  font-size: 0.75rem;
}

.mobile-team-stats {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.mobile-stat {
  text-align: center;

  &-number {
    display: block;
    font-weight: 700;
    font-size: 0.875rem;
    color: $dark;
  }

  &-label {
    display: block;
    font-size: 0.625rem;
    color: $gray;
    margin-top: 0.125rem;
  }
}

.mobile-expand-icon {
  color: $gray;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.mobile-match-history {
  border-top: 1px solid $border;
  background: $light-gray;
  padding: 1rem;
}

.mobile-match-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h6 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: $dark;
  }
}

.mobile-match-summary {
  display: flex;
  gap: 0.5rem;
}

// Mobile contact, competition filter, and matches
.mobile-team-contact-expanded {
  margin-bottom: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  h6 {
    margin: 0 0 0.75rem 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: $dark;
    display: flex;
    align-items: center;

    i {
      margin-right: 0.375rem;
      color: $info;
      padding: 0.25rem;
      background: rgba(59, 130, 246, 0.1);
      border-radius: 4px;
      font-size: 0.625rem;
    }
  }
}

.mobile-contact-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: $dark;
  padding: 0.375rem 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid rgba(226, 232, 240, 0.8);

  i {
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $info;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 3px;
    font-size: 0.625rem;
    flex-shrink: 0;
  }
}

.mobile-competition-filter-section {
  margin-bottom: 1rem;
}

.mobile-competition-filter {
  width: 100%;
}

.mobile-matches-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mobile-match-card {
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid $border;
}

.mobile-match-header-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.mobile-match-date {
  font-weight: 600;
  color: $dark;
  font-size: 0.75rem;
}

.mobile-match-result {
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.625rem;

  &.match-win { @include badge(#d1fae5, #065f46); }
  &.match-loss { @include badge(#fee2e2, #991b1b); }
  &.match-draw { @include badge(#fef3c7, #92400e); }
}

.mobile-match-teams-stacked {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mobile-team-score-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.375rem;
  background: $light-gray;
  border-radius: 3px;
  font-size: 0.75rem;
}

.mobile-team-name {
  color: $dark;
}

.mobile-team-score {
  font-weight: 700;
  color: $dark;
}

.mobile-player-performance {
  border-top: 1px solid $border;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.mobile-performance-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: $dark;
  font-size: 0.75rem;

  i {
    color: $warning;
  }
}

.mobile-performance-events {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.mobile-performance-event {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.mobile-event-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
}

.mobile-event-count {
  font-size: 0.5rem;
  opacity: 0.8;
  margin-left: 0.125rem;
}

.mobile-event-value {
  background: $success;
  color: white;
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  font-weight: 700;
  font-size: 0.625rem;
}

.mobile-motm-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.5rem;
  background: linear-gradient(135deg, $warning 0%, darken($warning, 10%) 100%);
  border-radius: 4px;
}

.mobile-motm-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: white;
  font-weight: 600;
  font-size: 0.625rem;

  i {
    color: white;
    font-size: 0.75rem;
  }
}

.mobile-motm-points {
  color: white;
  font-weight: 700;
  font-size: 0.625rem;
}

.mobile-show-more {
  text-align: center;
  margin-top: 0.75rem;
  color: $gray;
  font-size: 0.75rem;
}
</style>
